import type { ButtonHTMLAttributes } from "react"
import { cn } from "@/lib/utils"

/**
 * Button kinds (refined-system §5.4). `decision` (cobalt) is only the "yes" of something that needs the viewer.
 * Old names stay as aliases for one wave: `ghost` = `quiet`, `danger` = `destructive`, `dark` = `primary`,
 * `warn` = `decision`.
 */
type Variant =
  | "primary"
  | "decision"
  | "secondary"
  | "quiet"
  | "destructive"
  | "ghost"
  | "dark"
  | "warn"
  | "danger"
type Size = "sm" | "md" | "lg" | "touch"

const primary = "bg-ink font-semibold text-white hover:bg-ink/88"
const decision = "bg-needs font-semibold text-on-needs hover:bg-needs-deep"
const quiet = "font-normal text-muted hover:bg-fill hover:text-ink"
const destructive = "border border-line-control bg-transparent font-normal text-blocked hover:bg-blocked-soft"

const variants: Record<Variant, string> = {
  primary,
  decision,
  secondary: "border border-line-control bg-transparent font-normal text-ink hover:bg-fill",
  quiet,
  destructive,
  ghost: quiet,
  dark: primary,
  warn: decision,
  danger: destructive,
}

/** On a phone, `sm` and `md` grow to 40px with a 44px hit area (`tap`): callers never pick a phone size. */
const sizes: Record<Size, string> = {
  sm: "tap h-7 px-2.5 text-[13px] max-sm:h-10 max-sm:px-3.5 max-sm:text-[15px]",
  md: "tap h-8 px-3.5 text-[14px] max-sm:h-10 max-sm:text-[15px]",
  lg: "tap h-10 px-3.5 text-[14px] max-sm:text-[15px]",
  touch: "tap h-10 px-3.5 text-[15px]",
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant | undefined
  size?: Size | undefined
}

export function Button({
  variant = "primary",
  size = "lg",
  className,
  type = "button",
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn(
        "inline-flex shrink-0 items-center justify-center gap-1.5 rounded-control whitespace-nowrap",
        "transition-[background-color,transform] duration-[120ms] ease-minga active:scale-[.98]",
        "disabled:pointer-events-none disabled:opacity-50",
        sizes[size],
        variants[variant],
        className,
      )}
      {...props}
    />
  )
}
